import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { patientQueryKeys, patientsApi } from "../../../shared/api/patients";
import type {
  AlertCreateRequest,
  AlertSeverity,
  AlertType,
} from "../../../shared/types/patient";
import {
  alertSeverityLabel,
  alertTypeLabel,
  formatDateTime,
} from "../../../shared/format";
import { apiError } from "../../../shared/api/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

function severityVariant(
  severity: AlertSeverity,
): "destructive" | "warning" | "muted" {
  switch (severity.toLowerCase()) {
    case "critical":
    case "high":
      return "destructive";
    case "warning":
    case "medium":
      return "warning";
    default:
      return "muted";
  }
}

export function AlertsPanel({ patientId }: { patientId: string }) {
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);

  const list = useQuery({
    queryKey: patientQueryKeys.alerts(patientId),
    queryFn: () => patientsApi.listAlerts(patientId),
  });

  const ack = useMutation({
    mutationFn: (id: string) => patientsApi.acknowledgeAlert(patientId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: patientQueryKeys.alerts(patientId) }),
  });
  const dismiss = useMutation({
    mutationFn: (id: string) => patientsApi.dismissAlert(patientId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: patientQueryKeys.alerts(patientId) }),
  });

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle>Cảnh báo lâm sàng</CardTitle>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => setAdding((v) => !v)}
        >
          {adding ? "Đóng" : "+ Thêm cảnh báo"}
        </Button>
      </CardHeader>

      <CardContent className="space-y-3">
        {adding && (
          <AddAlertForm
            patientId={patientId}
            onDone={() => {
              setAdding(false);
              qc.invalidateQueries({ queryKey: patientQueryKeys.alerts(patientId) });
            }}
          />
        )}

        {list.isLoading && <p className="text-sm text-muted-foreground">Đang tải…</p>}
        {list.isError && (
          <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {apiError(list.error).detail}
          </div>
        )}
        {list.data && list.data.length === 0 && !adding && (
          <p className="text-sm text-muted-foreground">
            Không có cảnh báo nào đang hoạt động.
          </p>
        )}

        <div className="space-y-2">
          {list.data?.map((a) => (
            <div
              key={a.id}
              className="flex flex-col gap-3 rounded-md border bg-card p-3 sm:flex-row sm:items-start sm:justify-between"
            >
              <div className="min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-foreground">
                  <Badge variant={severityVariant(a.severity)}>
                    {alertSeverityLabel[a.severity]}
                  </Badge>
                  <span>{a.titleVi}</span>
                </div>
                <div className="text-sm text-muted-foreground">
                  {alertTypeLabel[a.alertType]}
                  {a.descriptionVi && ` · ${a.descriptionVi}`}
                  {a.expiresAt && ` · Hết hạn ${formatDateTime(a.expiresAt)}`}
                  {a.acknowledgedAt && ` · Đã xác nhận ${formatDateTime(a.acknowledgedAt)}`}
                </div>
              </div>
              <div className="flex shrink-0 flex-wrap items-center gap-2">
                {!a.acknowledgedAt && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={ack.isPending}
                    onClick={() => ack.mutate(a.id)}
                  >
                    Xác nhận
                  </Button>
                )}
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={dismiss.isPending}
                  onClick={() => dismiss.mutate(a.id)}
                >
                  Đóng
                </Button>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function AddAlertForm({ patientId, onDone }: { patientId: string; onDone: () => void }) {
  const [data, setData] = useState<AlertCreateRequest>({
    alertType: "CUSTOM",
    severity: "WARNING",
    titleVi: "",
    descriptionVi: "",
    expiresAt: null,
  });
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: (req: AlertCreateRequest) => patientsApi.createAlert(patientId, req),
    onSuccess: () => onDone(),
    onError: (e) => setError(apiError(e).detail),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!data.titleVi.trim()) {
          setError("Vui lòng nhập tiêu đề cảnh báo");
          return;
        }
        setError(null);
        create.mutate(data);
      }}
      className="space-y-3 rounded-md bg-muted p-4"
    >
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor="al-type">Loại cảnh báo</Label>
          <Select
            value={data.alertType}
            onValueChange={(value) =>
              setData((d) => ({ ...d, alertType: value as AlertType }))
            }
          >
            <SelectTrigger id="al-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(alertTypeLabel) as AlertType[]).map((t) => (
                <SelectItem key={t} value={t}>
                  {alertTypeLabel[t]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="al-severity">Mức độ</Label>
          <Select
            value={data.severity}
            onValueChange={(value) =>
              setData((d) => ({ ...d, severity: value as AlertSeverity }))
            }
          >
            <SelectTrigger id="al-severity">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(alertSeverityLabel) as AlertSeverity[]).map((s) => (
                <SelectItem key={s} value={s}>
                  {alertSeverityLabel[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="al-title">Tiêu đề *</Label>
          <Input
            id="al-title"
            type="text"
            value={data.titleVi}
            onChange={(e) => setData((d) => ({ ...d, titleVi: e.target.value }))}
            placeholder="VD: Đang mang thai 24 tuần"
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="al-desc">Mô tả</Label>
        <Input
          id="al-desc"
          type="text"
          value={data.descriptionVi ?? ""}
          onChange={(e) =>
            setData((d) => ({ ...d, descriptionVi: e.target.value || null }))
          }
        />
      </div>
      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}
      <div className="flex flex-wrap justify-end gap-2">
        <Button type="button" variant="secondary" onClick={onDone}>
          Hủy
        </Button>
        <Button type="submit" disabled={create.isPending}>
          {create.isPending ? "Đang lưu…" : "Thêm"}
        </Button>
      </div>
    </form>
  );
}
